// src/hooks/useBCPSavingStrategy.ts - Orchestrated saving strategy for BCP form
import { useMemo } from 'react';
import { FormState, RiskState, SavingStrategy, SaveStatus } from '../types/bcp';
import { useAutoSave } from './useAutoSave';
import { useManualSave } from './useManualSave';
import { useCriticalSave } from './useCriticalSave';

// Re-export SaveStatus for backward compatibility
export type { SaveStatus } from '../types/bcp';

export const useBCPSavingStrategy = (
  bcpId: string | undefined,
  isEditing: boolean,
  formData: FormState,
  risks: RiskState,
  originalData: any
): SavingStrategy => {
  // Individual saving hooks
  const { autoSave, autoSaveStatus } = useAutoSave(bcpId, isEditing);
  const { manualSave, manualSaveStatus } = useManualSave(bcpId, formData);
  const { criticalSave, criticalSaveStatus } = useCriticalSave(bcpId, isEditing, risks);

  // Combined saving status
  const savingStatus: SaveStatus = useMemo(() => ({
    isAutoSaving: autoSaveStatus.isAutoSaving,
    isManualSaving: manualSaveStatus.isManualSaving,
    isCriticalSaving: criticalSaveStatus.isCriticalSaving,
    lastSaved: autoSaveStatus.lastSaved || manualSaveStatus.lastSaved || criticalSaveStatus.lastSaved,
    errors: {
      ...autoSaveStatus.errors,
      ...manualSaveStatus.errors,
      ...criticalSaveStatus.errors
    }
  }), [autoSaveStatus, manualSaveStatus, criticalSaveStatus]);

  return {
    autoSave,
    manualSave,
    criticalSave,
    savingStatus
  };
};